import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  ButtonBase,
  InputAdornment,
  List,
  ListItemButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import PublicIcon from "@mui/icons-material/Public";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import {
  getCountryOptions,
  getDisplayCurrency,
  loadSupportedCurrencies,
  setVisitorCountry,
  useDisplayCurrency,
} from "utils/currency";

const Flag = ({ code, size = 20 }) => {
  const lc = String(code || "").toLowerCase();
  return (
    <img
      src={`https://flagcdn.com/w20/${lc}.png`}
      srcSet={`https://flagcdn.com/w40/${lc}.png 2x`}
      width={size}
      height={Math.round(size * 0.75)}
      alt=""
      loading="lazy"
      style={{ objectFit: "cover", borderRadius: 2, flexShrink: 0 }}
      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
    />
  );
};

// Country + currency dropdown for the header and footer. Shows the visitor's
// detected (or chosen) country flag and the currency prices are shown in.
const CurrencyPicker = ({ color = "inherit", alignSelf = "flex-start" }) => {
  const { country } = useDisplayCurrency();
  const current = getDisplayCurrency();
  const [anchor, setAnchor] = useState(null);
  const [query, setQuery] = useState("");
  const [supported, setSupported] = useState(null);

  useEffect(() => {
    if (anchor && !supported) loadSupportedCurrencies().then((s) => s && setSupported(s));
  }, [anchor, supported]);

  const options = useMemo(
    () => (anchor ? getCountryOptions(supported) : []),
    [anchor, supported, current],
  );
  const q = query.trim().toLowerCase();
  const filtered = q
    ? options.filter(
        (o) =>
          o.name.toLowerCase().includes(q) ||
          o.currency.toLowerCase().includes(q) ||
          o.symbol.toLowerCase().includes(q),
      )
    : options;

  const close = () => { setAnchor(null); setQuery(""); };
  const choose = (code) => { setVisitorCountry(code); close(); };

  return (
    <>
      <Tooltip title="Select your currency">
        <ButtonBase
          onClick={(e) => setAnchor(e.currentTarget)}
          aria-label="Choose country and currency"
          sx={{ display: "flex", alignItems: "center", alignSelf, width: "fit-content", gap: 0.5, color, px: 0.5, py: 0.25, borderRadius: 1, fontSize: 13 }}
        >
          {country ? <Flag code={country} size={19} /> : <PublicIcon sx={{ fontSize: 17 }} />}
          <span style={{ fontWeight: 600 }}>{current}</span>
          <KeyboardArrowDownIcon sx={{ fontSize: 17 }} />
        </ButtonBase>
      </Tooltip>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 320, maxWidth: "92vw", mt: 0.5 } } }}
      >
        <Box sx={{ p: 0.75, pb: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder="Search country or currency"
            sx={{
              "& .MuiInputBase-root": { fontSize: 13, minHeight: 0 },
              "& .MuiInputBase-input": { fontSize: "13px !important", py: "4px", height: "auto" },
              "& .MuiInputBase-input::placeholder": { fontSize: "13px", opacity: 0.6 },
            }}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <List dense disablePadding sx={{ maxHeight: 320, overflowY: "auto", pb: 0.5 }}>
          {filtered.map((o) => (
            <ListItemButton
              key={o.code}
              selected={o.code === country}
              onClick={() => choose(o.code)}
              sx={{ gap: 1, py: 0.25, px: 1.5, minHeight: 0 }}
            >
              <Flag code={o.code} size={18} />
              <Typography sx={{ flex: 1, fontSize: 13 }} noWrap>
                {o.name}
              </Typography>
              <Typography sx={{ fontSize: 12, opacity: 0.7, whiteSpace: "nowrap" }}>
                {o.symbol === o.currency ? o.currency : `${o.currency} (${o.symbol})`}
              </Typography>
            </ListItemButton>
          ))}
          {filtered.length === 0 && (
            <Typography sx={{ p: 2, fontSize: 14, opacity: 0.7 }}>No matches</Typography>
          )}
        </List>
      </Popover>
    </>
  );
};

export default CurrencyPicker;
